import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Autocomplete,
  FormHelperText,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  ViewWeek as ViewWeekIcon,
  DateRange as DateRangeIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  ContentCopy as ContentCopyIcon,
  MoneyOff as MoneyOffIcon,
  AttachMoney as AttachMoneyIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { amenityAPI, amenityScheduleAPI, membershipTierAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import BookAmenityDialog from './BookAmenityDialog';
import { formatDate, isDateOnly, formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import {
  Amenity,
  AmenitySchedule,
  AmenitySchedulePattern,
  TimeSlot,
  DaySchedule,
  SlotStatus,
  PricingTier,
  MembershipTier,
  TierAccessLevel,
  UserRole
} from '../../types';

const AmenitySchedulePage: React.FC = () => {
  const { amenityId } = useParams<{ amenityId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Ref for calendar container
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  // Ref for dialog content
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // State for amenity and schedules
  const [amenity, setAmenity] = useState<Amenity | null>(null);
  const [schedules, setSchedules] = useState<AmenitySchedule[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCopySchedulesDialog, setOpenCopySchedulesDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<AmenitySchedule | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Booking dialog state
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [bookingDialogDate, setBookingDialogDate] = useState<Date | null>(null);
  const [bookingDialogSlot, setBookingDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);

  // Copy schedules state
  const [availableAmenities, setAvailableAmenities] = useState<Amenity[]>([]);
  const [selectedSourceAmenity, setSelectedSourceAmenity] = useState<string>('');
  const [copyingSchedules, setCopyingSchedules] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'daily' as 'daily' | 'weekly' | 'monthly',
    effectiveStartDate: '',
    effectiveEndDate: '',
    minBookingDurationMinutes: 15,
    maxConsecutiveSlots: 8,
    maxBookingDurationMinutes: 120, // Auto-calculated field
    priority: 1,
    active: true,
    courtUseRestrictions: false,
    minPlayers: 1,
    maxPlayers: 2,
    availableToTiers: [] as string[],
    dailySchedule: [] as TimeSlot[],
    weeklySchedule: [] as DaySchedule[],
    monthlySchedule: [] as { dayOfMonth: number; slots: TimeSlot[] }[]
  });

  // Time slot form state
  const [newSlot, setNewSlot] = useState({
    startTime: '',
    endTime: '',
    status: SlotStatus.AVAILABLE,
    pricingTier: PricingTier.NORMAL,
    priceOverride: ''
  });

  // Weekly schedule state
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(0);
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState(1);

  // Time slot editing state
  const [editingSlot, setEditingSlot] = useState<{
    index: number;
    dayOfWeek?: number;
    dayOfMonth?: number;
    slot: TimeSlot;
  } | null>(null);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarSlots, setCalendarSlots] = useState<TimeSlot[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [minBookingDuration, setMinBookingDuration] = useState<number>(30);
  const [maxBookingDuration, setMaxBookingDuration] = useState<number>(120);

  // Date picker state
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<string>('');

  const daysOfWeek = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  // Fetch data on component mount
  useEffect(() => {
    if (amenityId) {
      fetchAmenity();
      fetchSchedules();
      fetchCalendarData(selectedDate);
      fetchMembershipTiers();
    }
  }, [amenityId]);

  // Fetch calendar data when date changes
  useEffect(() => {
    if (amenityId && selectedDate) {
      fetchCalendarData(selectedDate);
    }
  }, [selectedDate]);

  const fetchAmenity = async () => {
    try {
      if (!amenityId) return;
      const response = await amenityAPI.getAmenityById(amenityId);
      setAmenity(response.data);
    } catch (error: any) {
      console.error('Error fetching amenity:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch amenity details';
      setError(errorMessage);
    }
  };

  const fetchSchedules = async () => {
    try {
      if (!amenityId) return;
      setLoading(true);
      const response = await amenityScheduleAPI.getAmenitySchedules(amenityId);
      setSchedules(response.data);
    } catch (error: any) {
      console.error('Error fetching schedules:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch schedules';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      // Filter out the current amenity from the list
      const filteredAmenities = response.data.filter((a: Amenity) => a.id !== amenityId);
      setAvailableAmenities(filteredAmenities);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
    }
  };

  const fetchMembershipTiers = async () => {
    try {
      const response = await membershipTierAPI.getMembershipTiers();
      setMembershipTiers(response.data);
    } catch (error: any) {
      console.error('Error fetching membership tiers:', error);
    }
  };

  const fetchCalendarData = async (date: Date) => {
    try {
      if (!amenityId) return;
      setCalendarLoading(true);
      setCalendarError(null);

      // Format date as YYYY-MM-DD
      const dateString = formatDateForInput(date);

      const response = await amenityScheduleAPI.getAvailableSlots(amenityId, dateString);

      // The backend returns slots under a 'slots' property
      const slots = response.data?.slots || response.data;
      const minDuration = response.data?.minBookingDuration || 30;
      const maxDuration = response.data?.maxBookingDuration || 120;

      if (Array.isArray(slots)) {
        setCalendarSlots(slots);
        setMinBookingDuration(minDuration);
        setMaxBookingDuration(maxDuration);

        // Auto-scroll to first scheduled slot after a short delay to ensure DOM is updated
        setTimeout(() => {
          scrollToFirstScheduledSlot();
        }, 100);
      } else {
        setCalendarSlots([]);
      }
    } catch (error: any) {
      console.error('Error fetching calendar data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch calendar data';
      setCalendarError(errorMessage);
      setCalendarSlots([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const openDatePickerDialog = () => {
    setTempSelectedDate(formatDateForInput(selectedDate));
    setOpenDatePicker(true);
  };

  const handleDatePickerConfirm = () => {
    if (tempSelectedDate) {
      const newDate = parseDateOnlyString(tempSelectedDate);
      setSelectedDate(newDate);
    }
    setOpenDatePicker(false);
  };

  const handleDatePickerCancel = () => {
    setOpenDatePicker(false);
    setTempSelectedDate('');
  };

  const scrollToBusinessHours = () => {
    if (!calendarContainerRef.current) return;

    // Scroll to 8 AM (business hours start) within the calendar container
    const businessHourElement = calendarContainerRef.current.querySelector('[data-time="8:00"]');
    if (businessHourElement) {
      const blockElement = businessHourElement.parentElement;
      if (blockElement) {
        const containerRect = calendarContainerRef.current.getBoundingClientRect();
        const blockRect = blockElement.getBoundingClientRect();
        const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

        calendarContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollToFirstScheduledSlot = () => {
    if (!calendarContainerRef.current) return;

    // Find the first time block that has a schedule
    const timeBlocks = generateTimeBlocks();
    const firstScheduledBlock = timeBlocks.find(block => block.hasSchedule);

    if (firstScheduledBlock) {
      // Find the time element within the calendar container
      const scheduledElement = calendarContainerRef.current.querySelector(`[data-time="${firstScheduledBlock.startTime}"]`);
      if (scheduledElement) {
        const blockElement = scheduledElement.parentElement;
        if (blockElement) {
          // Calculate position relative to calendar container
          const containerRect = calendarContainerRef.current.getBoundingClientRect();
          const blockRect = blockElement.getBoundingClientRect();
          const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

          // Scroll within the calendar container
          calendarContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
        return;
      }
    }

    // If no scheduled slots found, default to 8 AM
    const eightAmElement = calendarContainerRef.current.querySelector(`[data-time="8:00"]`);
    if (eightAmElement) {
      const blockElement = eightAmElement.parentElement;
      if (blockElement) {
        const containerRect = calendarContainerRef.current.getBoundingClientRect();
        const blockRect = blockElement.getBoundingClientRect();
        const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

        calendarContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  };

  // Helper function to check for overlapping time slots
  const checkForOverlaps = (newSlot: TimeSlot, existingSlots: TimeSlot[]) => {
    const newStartMinutes = timeToMinutes(newSlot.startTime);
    const newEndMinutes = timeToMinutes(newSlot.endTime);

    return existingSlots.some(existingSlot => {
      const existingStartMinutes = timeToMinutes(existingSlot.startTime);
      const existingEndMinutes = timeToMinutes(existingSlot.endTime);

      // Check if time ranges overlap
      return (
        (newStartMinutes >= existingStartMinutes && newStartMinutes < existingEndMinutes) ||
        (newEndMinutes > existingStartMinutes && newEndMinutes <= existingEndMinutes) ||
        (newStartMinutes <= existingStartMinutes && newEndMinutes >= existingEndMinutes)
      );
    });
  };

  // Helper function to check for overlaps with existing schedules
  const checkForScheduleOverlaps = async (formData: any) => {
    try {
      if (!amenityId) return null;

      // Get all existing schedules for this amenity
      const response = await amenityScheduleAPI.getAmenitySchedules(amenityId);
      const existingSchedules = response.data.filter((schedule: AmenitySchedule) =>
        // Exclude the current schedule if editing
        !isEditing || schedule.id !== selectedSchedule?.id
      );

      // Check for overlaps based on schedule frequency
      for (const existingSchedule of existingSchedules) {
        // Only check active schedules that have overlapping effective date ranges
        if (!existingSchedule.active) continue;

        const newStartDate = new Date(formData.effectiveStartDate);
        const newEndDate = formData.effectiveEndDate ? new Date(formData.effectiveEndDate) : null;
        const existingStartDate = new Date(existingSchedule.effectiveStartDate);
        const existingEndDate = existingSchedule.effectiveEndDate ? new Date(existingSchedule.effectiveEndDate) : null;

        // Check if date ranges overlap
        // Range 1: [newStartDate, newEndDate] (newEndDate can be null for open-ended)
        // Range 2: [existingStartDate, existingEndDate] (existingEndDate can be null for open-ended)

        let dateRangesOverlap = true;

        // If new range ends before existing range starts (and new range has an end date)
        if (newEndDate && newEndDate < existingStartDate) {
          dateRangesOverlap = false;
        }

        // If existing range ends before new range starts (and existing range has an end date)
        if (existingEndDate && existingEndDate < newStartDate) {
          dateRangesOverlap = false;
        }

        if (!dateRangesOverlap) continue;

        // Check for time slot overlaps based on frequency
        if (formData.frequency === 'daily' && existingSchedule.schedulePattern.frequency === 'daily') {
          const newSlots = formData.dailySchedule;
          const existingSlots = existingSchedule.schedulePattern.dailySchedule || [];

          for (const newSlot of newSlots) {
            if (checkForOverlaps(newSlot, existingSlots)) {
              return {
                scheduleName: existingSchedule.name,
                timeRange: `${newSlot.startTime} - ${newSlot.endTime}`,
                frequency: 'daily'
              };
            }
          }
        } else if (formData.frequency === 'weekly' && existingSchedule.schedulePattern.frequency === 'weekly') {
          const newWeeklySchedule = formData.weeklySchedule;
          const existingWeeklySchedule = existingSchedule.schedulePattern.weeklySchedule || [];

          for (const newDay of newWeeklySchedule) {
            const existingDay = existingWeeklySchedule.find((d: DaySchedule) => d.dayOfWeek === newDay.dayOfWeek);
            if (existingDay) {
              for (const newSlot of newDay.slots) {
                if (checkForOverlaps(newSlot, existingDay.slots)) {
                  const dayName = daysOfWeek[newDay.dayOfWeek];
                  return {
                    scheduleName: existingSchedule.name,
                    timeRange: `${dayName} ${newSlot.startTime} - ${newSlot.endTime}`,
                    frequency: 'weekly'
                  };
                }
              }
            }
          }
        } else if (formData.frequency === 'monthly' && existingSchedule.schedulePattern.frequency === 'monthly') {
          const newMonthlySchedule = formData.monthlySchedule;
          const existingMonthlySchedule = existingSchedule.schedulePattern.monthlySchedule || [];

          for (const newMonth of newMonthlySchedule) {
            const existingMonth = existingMonthlySchedule.find((m: any) => m.dayOfMonth === newMonth.dayOfMonth);
            if (existingMonth) {
              for (const newSlot of newMonth.slots) {
                if (checkForOverlaps(newSlot, existingMonth.slots)) {
                  return {
                    scheduleName: existingSchedule.name,
                    timeRange: `Day ${newMonth.dayOfMonth} ${newSlot.startTime} - ${newSlot.endTime}`,
                    frequency: 'monthly'
                  };
                }
              }
            }
          }
        }
      }

      return null; // No overlaps found
    } catch (error) {
      console.error('Error checking for schedule overlaps:', error);
      return null;
    }
  };

  // Helper function to convert time string (HH:MM) to minutes since midnight
  const timeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to convert minutes since midnight to time string (HH:MM)
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to format time for display (12-hour format)
  const formatDisplayTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Generate time blocks for the calendar view
  const generateTimeBlocks = () => {
    const intervalMinutes = minBookingDuration;

    // Show full day from 12 AM to 12 AM (24 hours)
    const earliestMinutes = 0; // 12:00 AM
    const latestMinutes = 24 * 60; // 12:00 AM next day

    const timeBlocks = [];

    for (let currentMinutes = earliestMinutes; currentMinutes < latestMinutes; currentMinutes += intervalMinutes) {
      const blockStartTime = minutesToTime(currentMinutes);
      const blockEndTime = minutesToTime(currentMinutes + intervalMinutes);

      // Find the first slot that contains this time block (no overlaps expected)
      const matchingSlot = calendarSlots.find(slot => {
        const slotStart = timeToMinutes(slot.startTime);
        const slotEnd = timeToMinutes(slot.endTime);
        return currentMinutes >= slotStart && currentMinutes < slotEnd;
      });

      timeBlocks.push({
        startTime: blockStartTime,
        endTime: blockEndTime,
        displayTime: formatDisplayTime(blockStartTime),
        status: matchingSlot?.status || SlotStatus.UNAVAILABLE,
        pricingTier: matchingSlot?.pricingTier || PricingTier.NORMAL,
        priceOverride: matchingSlot?.priceOverride,
        hasSchedule: !!matchingSlot,
        bookedBy: matchingSlot?.bookedBy,
        bookedByEmail: matchingSlot?.bookedByEmail,
        bookingId: matchingSlot?.bookingId, // Add bookingId for navigation
        bookingCount: matchingSlot?.bookingCount,
        programName: matchingSlot?.programName,
        programInstanceId: matchingSlot?.programInstanceId,
        coachingSessionName: matchingSlot?.coachingSessionName
      });
    }

    return timeBlocks;
  };

  const getStatusColor = (status: SlotStatus, hasSchedule: boolean) => {
    if (!hasSchedule) {
      return '#f5f5f5'; // Light gray for no schedule
    }

    switch (status) {
      case SlotStatus.AVAILABLE:
        return '#4caf50'; // Green
      case SlotStatus.BOOKED:
        return '#f44336'; // Red
      case SlotStatus.ON_HOLD:
        return '#ff9800'; // Orange
      case SlotStatus.UNAVAILABLE:
        return '#9e9e9e'; // Gray
      case SlotStatus.OUT_OF_SCHEDULE:
        return '#9c27b0'; // Purple for out of schedule
      default:
        return '#f5f5f5';
    }
  };

  const getStatusTextColor = (status: SlotStatus, hasSchedule: boolean) => {
    if (!hasSchedule) {
      return '#666';
    }

    switch (status) {
      case SlotStatus.AVAILABLE:
      case SlotStatus.BOOKED:
        return '#fff';
      case SlotStatus.ON_HOLD:
      case SlotStatus.UNAVAILABLE:
      case SlotStatus.OUT_OF_SCHEDULE:
        return '#fff';
      default:
        return '#666';
    }
  };

  const handleCreateSchedule = () => {
    setIsEditing(false);
    setError(null);
    setEditingSlot(null);
    setFormData({
      name: '',
      description: '',
      frequency: 'daily',
      effectiveStartDate: '',
      effectiveEndDate: '',
      minBookingDurationMinutes: 15,
      maxConsecutiveSlots: 8,
      maxBookingDurationMinutes: 120,
      priority: 1,
      active: true,
      courtUseRestrictions: false,
      minPlayers: 1,
      maxPlayers: 2,
      availableToTiers: [],
      dailySchedule: [],
      weeklySchedule: [],
      monthlySchedule: []
    });
    setNewSlot({
      startTime: '',
      endTime: '',
      status: SlotStatus.AVAILABLE,
      pricingTier: PricingTier.NORMAL,
      priceOverride: ''
    });
    setOpenCreateDialog(true);
  };

  const handleEditSchedule = (schedule: AmenitySchedule) => {
    setIsEditing(true);
    setSelectedSchedule(schedule);
    setError(null);
    setEditingSlot(null);

    setFormData({
      name: schedule.name,
      description: schedule.description || '',
      frequency: schedule.schedulePattern.frequency,
      effectiveStartDate: typeof schedule.effectiveStartDate === 'string' && schedule.effectiveStartDate.includes('-') && schedule.effectiveStartDate.length === 10
        ? schedule.effectiveStartDate
        : formatDateForInput(new Date(schedule.effectiveStartDate)),
      effectiveEndDate: schedule.effectiveEndDate
        ? (typeof schedule.effectiveEndDate === 'string' && schedule.effectiveEndDate.includes('-') && schedule.effectiveEndDate.length === 10
          ? schedule.effectiveEndDate
          : formatDateForInput(new Date(schedule.effectiveEndDate)))
        : '',
      minBookingDurationMinutes: schedule.minBookingDurationMinutes,
      maxConsecutiveSlots: schedule.maxConsecutiveSlots || Math.ceil(schedule.maxBookingDurationMinutes / schedule.minBookingDurationMinutes),
      maxBookingDurationMinutes: schedule.maxBookingDurationMinutes,
      priority: schedule.priority,
      active: schedule.active,
      courtUseRestrictions: schedule.courtUseRestrictions || false,
      minPlayers: schedule.minPlayers || 1,
      maxPlayers: schedule.maxPlayers || 2,
      availableToTiers: schedule.availableToTiers || [],
      dailySchedule: schedule.schedulePattern.dailySchedule || [],
      weeklySchedule: schedule.schedulePattern.weeklySchedule || [],
      monthlySchedule: schedule.schedulePattern.monthlySchedule || []
    });
    setOpenCreateDialog(true);
  };

  const handleDeleteSchedule = (schedule: AmenitySchedule) => {
    setSelectedSchedule(schedule);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!selectedSchedule) return;

    try {
      await amenityScheduleAPI.deleteSchedule(selectedSchedule.id);
      await fetchSchedules();
      fetchCalendarData(selectedDate);
      setOpenDeleteDialog(false);
      setSelectedSchedule(null);
      setSuccessMessage('Schedule deleted successfully');
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      // Extract the error message from the response
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete schedule';
      setError(errorMessage);
    }
  };

  const handleCopySchedules = () => {
    setSelectedSourceAmenity('');
    setError(null);
    fetchAvailableAmenities();
    setOpenCopySchedulesDialog(true);
  };

  const confirmCopySchedules = async () => {
    if (!selectedSourceAmenity || !amenityId) {
      setError('Please select an amenity to copy schedules from');
      return;
    }

    try {
      setCopyingSchedules(true);
      setError(null);

      // Get schedules from source amenity
      const sourceSchedulesResponse = await amenityScheduleAPI.getAmenitySchedules(selectedSourceAmenity);
      const sourceSchedules = sourceSchedulesResponse.data;

      if (sourceSchedules.length === 0) {
        setError('The selected amenity has no schedules to copy');
        setCopyingSchedules(false);
        return;
      }

      // Copy each schedule to the current amenity
      let copiedCount = 0;
      const errors: string[] = [];

      for (const sourceSchedule of sourceSchedules) {
        try {
          console.log(`Copying schedule "${sourceSchedule.name}" with active status:`, sourceSchedule.active);
          const scheduleData = {
            amenityId: amenityId,
            name: `${sourceSchedule.name} - Copy`,
            description: sourceSchedule.description || '',
            schedulePattern: sourceSchedule.schedulePattern,
            minBookingDurationMinutes: sourceSchedule.minBookingDurationMinutes,
            maxConsecutiveSlots: sourceSchedule.maxConsecutiveSlots,
            maxBookingDurationMinutes: sourceSchedule.maxBookingDurationMinutes,
            effectiveStartDate: sourceSchedule.effectiveStartDate,
            effectiveEndDate: sourceSchedule.effectiveEndDate,
            priority: sourceSchedule.priority,
            active: !!sourceSchedule.active, // Explicitly convert to boolean
            courtUseRestrictions: sourceSchedule.courtUseRestrictions || false,
            minPlayers: sourceSchedule.minPlayers,
            maxPlayers: sourceSchedule.maxPlayers,
            availableToTiers: sourceSchedule.availableToTiers
          };
          console.log('Schedule data being sent:', scheduleData);

          await amenityScheduleAPI.createSchedule(amenityId, scheduleData);
          copiedCount++;
        } catch (error: any) {
          console.error(`Error copying schedule ${sourceSchedule.name}:`, error);
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
          errors.push(`Failed to copy "${sourceSchedule.name}": ${errorMessage}`);
        }
      }

      // Show results
      if (copiedCount > 0) {
        setSuccessMessage(`Successfully copied ${copiedCount} schedule${copiedCount > 1 ? 's' : ''}`);
        await fetchSchedules();
        fetchCalendarData(selectedDate);
      }

      if (errors.length > 0) {
        setError(`Some schedules could not be copied:\n${errors.join('\n')}`);
      }

      setOpenCopySchedulesDialog(false);
      setSelectedSourceAmenity('');
    } catch (error: any) {
      console.error('Error copying schedules:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to copy schedules';
      setError(errorMessage);
    } finally {
      setCopyingSchedules(false);
    }
  };



  const handleSubmit = async () => {
    setError(null);

    // Ensure amenityId is available
    if (!amenityId) {
      setError('Amenity ID is missing. Please reload the page and try again.');
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      setError('Schedule name is required');
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.effectiveStartDate) {
      setError('Effective start date is required');
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate that at least one time slot is defined for the selected frequency
    let hasTimeSlots = false;
    switch (formData.frequency) {
      case 'daily':
        hasTimeSlots = formData.dailySchedule.length > 0;
        break;
      case 'weekly':
        hasTimeSlots = formData.weeklySchedule.some(day => day.slots.length > 0);
        break;
      case 'monthly':
        hasTimeSlots = formData.monthlySchedule.some(month => month.slots.length > 0);
        break;
    }

    if (!hasTimeSlots) {
      setError('At least one time slot must be defined for the schedule');
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Check for overlaps with existing schedules
    const overlapInfo = await checkForScheduleOverlaps(formData);
    if (overlapInfo) {
      setError(`The schedule for the time ${overlapInfo.timeRange} is already defined in "${overlapInfo.scheduleName}"`);
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const schedulePattern: AmenitySchedulePattern = {
        frequency: formData.frequency,
        effectiveStartDate: formData.effectiveStartDate,
        effectiveEndDate: formData.effectiveEndDate ? formData.effectiveEndDate : undefined
      };

      // Add schedule data based on frequency
      switch (formData.frequency) {
        case 'daily':
          schedulePattern.dailySchedule = formData.dailySchedule;
          break;
        case 'weekly':
          schedulePattern.weeklySchedule = formData.weeklySchedule;
          break;
        case 'monthly':
          schedulePattern.monthlySchedule = formData.monthlySchedule;
          break;
      }

      const scheduleData = {
        amenityId: amenityId!, // Add the amenity ID to the request body
        name: formData.name,
        description: formData.description,
        schedulePattern,
        minBookingDurationMinutes: formData.minBookingDurationMinutes,
        maxConsecutiveSlots: formData.maxConsecutiveSlots,
        maxBookingDurationMinutes: formData.maxConsecutiveSlots * formData.minBookingDurationMinutes, // Auto-calculated
        effectiveStartDate: formData.effectiveStartDate,
        effectiveEndDate: formData.effectiveEndDate || null, // Send null when empty to clear end date
        priority: formData.priority,
        active: formData.active,
        courtUseRestrictions: formData.courtUseRestrictions,
        minPlayers: formData.courtUseRestrictions ? formData.minPlayers : undefined,
        maxPlayers: formData.courtUseRestrictions ? formData.maxPlayers : undefined,
        availableToTiers: formData.availableToTiers.length > 0 ? formData.availableToTiers : undefined
      };

      if (isEditing && selectedSchedule) {
        await amenityScheduleAPI.updateSchedule(selectedSchedule.id, scheduleData);
        setSuccessMessage('Schedule updated successfully');
      } else {
        await amenityScheduleAPI.createSchedule(amenityId!, scheduleData);
        setSuccessMessage('Schedule created successfully');
      }

      await fetchSchedules();
      fetchCalendarData(selectedDate);
      setOpenCreateDialog(false);
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      // Extract the error message from the response
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      setError(errorMessage);
    }
  };

  const addTimeSlot = () => {
    if (!newSlot.startTime || !newSlot.endTime) {
      setError('Both start and end times are required');
      return;
    }

    // Validate that end time is after start time
    if (newSlot.startTime >= newSlot.endTime) {
      setError('End time must be after start time');
      return;
    }

    const slot: TimeSlot = {
      startTime: newSlot.startTime,
      endTime: newSlot.endTime,
      status: newSlot.status,
      pricingTier: newSlot.pricingTier,
      priceOverride: newSlot.priceOverride ? parseFloat(newSlot.priceOverride) : undefined
    };

    // Check for internal overlaps within the same schedule
    let existingSlots: TimeSlot[] = [];

    switch (formData.frequency) {
      case 'daily':
        existingSlots = formData.dailySchedule;
        break;
      case 'weekly':
        const daySchedule = formData.weeklySchedule.find(day => day.dayOfWeek === selectedDayOfWeek);
        existingSlots = daySchedule?.slots || [];
        break;
      case 'monthly':
        const monthSchedule = formData.monthlySchedule.find(month => month.dayOfMonth === selectedDayOfMonth);
        existingSlots = monthSchedule?.slots || [];
        break;
    }

    // Check for overlaps with existing slots in the same schedule
    if (checkForOverlaps(slot, existingSlots)) {
      setError('Overlapping schedules are not permitted');
      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    switch (formData.frequency) {
      case 'daily':
        setFormData({
          ...formData,
          dailySchedule: [...formData.dailySchedule, slot]
        });
        break;
      case 'weekly':
        const dayIndex = formData.weeklySchedule.findIndex(day => day.dayOfWeek === selectedDayOfWeek);
        if (dayIndex >= 0) {
          const updatedWeeklySchedule = [...formData.weeklySchedule];
          updatedWeeklySchedule[dayIndex].slots.push(slot);
          setFormData({
            ...formData,
            weeklySchedule: updatedWeeklySchedule
          });
        } else {
          setFormData({
            ...formData,
            weeklySchedule: [
              ...formData.weeklySchedule,
              { dayOfWeek: selectedDayOfWeek, slots: [slot] }
            ]
          });
        }
        break;
      case 'monthly':
        const monthIndex = formData.monthlySchedule.findIndex(month => month.dayOfMonth === selectedDayOfMonth);
        if (monthIndex >= 0) {
          const updatedMonthlySchedule = [...formData.monthlySchedule];
          updatedMonthlySchedule[monthIndex].slots.push(slot);
          setFormData({
            ...formData,
            monthlySchedule: updatedMonthlySchedule
          });
        } else {
          setFormData({
            ...formData,
            monthlySchedule: [
              ...formData.monthlySchedule,
              { dayOfMonth: selectedDayOfMonth, slots: [slot] }
            ]
          });
        }
        break;
    }

    setNewSlot({
      startTime: '',
      endTime: '',
      status: SlotStatus.AVAILABLE,
      pricingTier: PricingTier.NORMAL,
      priceOverride: ''
    });

    // Clear any previous error when a slot is successfully added
    setError(null);
  };

  // Helper function to check if the form is valid
  const isFormValid = () => {
    // Check mandatory fields
    if (!formData.name.trim() || !formData.effectiveStartDate) {
      return false;
    }

    // Check if at least one time slot is defined
    let hasTimeSlots = false;
    switch (formData.frequency) {
      case 'daily':
        hasTimeSlots = formData.dailySchedule.length > 0;
        break;
      case 'weekly':
        hasTimeSlots = formData.weeklySchedule.some(day => day.slots.length > 0);
        break;
      case 'monthly':
        hasTimeSlots = formData.monthlySchedule.some(month => month.slots.length > 0);
        break;
    }

    return hasTimeSlots;
  };

  // Helper function to get validation message
  const getValidationMessage = () => {
    const missingFields = [];

    if (!formData.name.trim()) {
      missingFields.push('Schedule name');
    }

    if (!formData.effectiveStartDate) {
      missingFields.push('Effective start date');
    }

    // Check if at least one time slot is defined
    let hasTimeSlots = false;
    switch (formData.frequency) {
      case 'daily':
        hasTimeSlots = formData.dailySchedule.length > 0;
        break;
      case 'weekly':
        hasTimeSlots = formData.weeklySchedule.some(day => day.slots.length > 0);
        break;
      case 'monthly':
        hasTimeSlots = formData.monthlySchedule.some(month => month.slots.length > 0);
        break;
    }

    if (!hasTimeSlots) {
      missingFields.push('At least one time slot');
    }

    if (missingFields.length === 0) {
      return '';
    }

    return `Required: ${missingFields.join(', ')}`;
  };

  const removeTimeSlot = (index: number, dayOfWeek?: number, dayOfMonth?: number) => {
    switch (formData.frequency) {
      case 'daily':
        const newDailySchedule = [...formData.dailySchedule];
        newDailySchedule.splice(index, 1);
        setFormData({
          ...formData,
          dailySchedule: newDailySchedule
        });
        break;
      case 'weekly':
        if (dayOfWeek !== undefined) {
          const dayIndex = formData.weeklySchedule.findIndex(day => day.dayOfWeek === dayOfWeek);
          if (dayIndex >= 0) {
            const updatedWeeklySchedule = [...formData.weeklySchedule];
            updatedWeeklySchedule[dayIndex].slots.splice(index, 1);
            setFormData({
              ...formData,
              weeklySchedule: updatedWeeklySchedule
            });
          }
        }
        break;
      case 'monthly':
        if (dayOfMonth !== undefined) {
          const monthIndex = formData.monthlySchedule.findIndex(month => month.dayOfMonth === dayOfMonth);
          if (monthIndex >= 0) {
            const updatedMonthlySchedule = [...formData.monthlySchedule];
            updatedMonthlySchedule[monthIndex].slots.splice(index, 1);
            setFormData({
              ...formData,
              monthlySchedule: updatedMonthlySchedule
            });
          }
        }
        break;
    }
  };

  const startEditingSlot = (index: number, slot: TimeSlot, dayOfWeek?: number, dayOfMonth?: number) => {
    setEditingSlot({
      index,
      dayOfWeek,
      dayOfMonth,
      slot: { ...slot }
    });
  };

  const cancelEditingSlot = () => {
    setEditingSlot(null);
  };

  const saveEditedSlot = () => {
    if (!editingSlot) return;

    const { index, dayOfWeek, dayOfMonth, slot } = editingSlot;

    switch (formData.frequency) {
      case 'daily':
        const newDailySchedule = [...formData.dailySchedule];
        newDailySchedule[index] = slot;
        setFormData({
          ...formData,
          dailySchedule: newDailySchedule
        });
        break;
      case 'weekly':
        if (dayOfWeek !== undefined) {
          const dayIndex = formData.weeklySchedule.findIndex(day => day.dayOfWeek === dayOfWeek);
          if (dayIndex >= 0) {
            const updatedWeeklySchedule = [...formData.weeklySchedule];
            updatedWeeklySchedule[dayIndex].slots[index] = slot;
            setFormData({
              ...formData,
              weeklySchedule: updatedWeeklySchedule
            });
          }
        }
        break;
      case 'monthly':
        if (dayOfMonth !== undefined) {
          const monthIndex = formData.monthlySchedule.findIndex(month => month.dayOfMonth === dayOfMonth);
          if (monthIndex >= 0) {
            const updatedMonthlySchedule = [...formData.monthlySchedule];
            updatedMonthlySchedule[monthIndex].slots[index] = slot;
            setFormData({
              ...formData,
              monthlySchedule: updatedMonthlySchedule
            });
          }
        }
        break;
    }

    setEditingSlot(null);
  };

  const updateEditingSlot = (field: keyof TimeSlot, value: any) => {
    if (!editingSlot) return;

    setEditingSlot({
      ...editingSlot,
      slot: {
        ...editingSlot.slot,
        [field]: value
      }
    });
  };

  const getSlotStatusColor = (status: SlotStatus) => {
    switch (status) {
      case SlotStatus.AVAILABLE:
        return 'success';
      case SlotStatus.BOOKED:
        return 'error';
      case SlotStatus.ON_HOLD:
        return 'warning';
      case SlotStatus.UNAVAILABLE:
        return 'default';
      default:
        return 'default';
    }
  };

  const getSlotStatusText = (status: SlotStatus) => {
    switch (status) {
      case SlotStatus.AVAILABLE:
        return 'Available';
      case SlotStatus.BOOKED:
        return 'Booked';
      case SlotStatus.ON_HOLD:
        return 'On Hold';
      case SlotStatus.UNAVAILABLE:
        return 'Unavailable';
      default:
        return status;
    }
  };

  const getPricingTierText = (tier: PricingTier) => {
    switch (tier) {
      case PricingTier.OFF_PEAK:
        return 'Off Peak';
      case PricingTier.NORMAL:
        return 'Normal';
      case PricingTier.PEAK:
        return 'Peak';
      default:
        return tier;
    }
  };

  const getPricingTierIcon = (tier: PricingTier) => {
    switch (tier) {
      case PricingTier.PEAK:
        return <AttachMoneyIcon sx={{ fontSize: 16, ml: 0.5 }} />;
      case PricingTier.OFF_PEAK:
        return <MoneyOffIcon sx={{ fontSize: 16, ml: 0.5 }} />;
      case PricingTier.NORMAL:
      default:
        return null;
    }
  };

  const renderTimeSlots = () => {
    const renderSlotCard = (slot: TimeSlot, index: number, dayOfWeek?: number, dayOfMonth?: number) => {
      const isEditing = editingSlot &&
        editingSlot.index === index &&
        editingSlot.dayOfWeek === dayOfWeek &&
        editingSlot.dayOfMonth === dayOfMonth;

      return (
        <Card key={index} sx={{ mb: 2, border: isEditing ? 2 : 1, borderColor: isEditing ? 'primary.main' : 'divider' }}>
          <CardContent sx={{ pb: 1 }}>
            {isEditing ? (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={editingSlot.slot.startTime}
                    onChange={(e) => updateEditingSlot('startTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="End Time"
                    type="time"
                    value={editingSlot.slot.endTime}
                    onChange={(e) => updateEditingSlot('endTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editingSlot.slot.status}
                      label="Status"
                      onChange={(e) => updateEditingSlot('status', e.target.value)}
                    >
                      <MenuItem value={SlotStatus.AVAILABLE}>Available</MenuItem>
                      <MenuItem value={SlotStatus.UNAVAILABLE}>Unavailable</MenuItem>
                      <MenuItem value={SlotStatus.ON_HOLD}>On Hold</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Pricing Tier</InputLabel>
                    <Select
                      value={editingSlot.slot.pricingTier}
                      label="Pricing Tier"
                      onChange={(e) => updateEditingSlot('pricingTier', e.target.value)}
                    >
                      <MenuItem value={PricingTier.OFF_PEAK}>Off Peak</MenuItem>
                      <MenuItem value={PricingTier.NORMAL}>Normal</MenuItem>
                      <MenuItem value={PricingTier.PEAK}>Peak</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Price Override"
                    type="number"
                    value={editingSlot.slot.priceOverride || ''}
                    onChange={(e) => updateEditingSlot('priceOverride', e.target.value ? parseFloat(e.target.value) : undefined)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" component="h3">
                    {slot.startTime} - {slot.endTime}
                  </Typography>
                  <Chip
                    label={(slot.status === SlotStatus.BOOKED || slot.status === SlotStatus.OUT_OF_SCHEDULE || slot.status === SlotStatus.ON_HOLD) && slot.bookedBy
                      ? `Booked by ${slot.bookedBy}`
                      : slot.status}
                    color={slot.status === SlotStatus.AVAILABLE ? 'success' :
                      slot.status === SlotStatus.ON_HOLD ? 'warning' :
                        slot.status === SlotStatus.BOOKED ? 'error' :
                          slot.status === SlotStatus.OUT_OF_SCHEDULE ? 'secondary' : 'default'}
                    size="small"
                  />
                </Box>
                {(slot.status === SlotStatus.BOOKED || slot.status === SlotStatus.OUT_OF_SCHEDULE || slot.status === SlotStatus.ON_HOLD) && slot.bookedBy && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Booked by: <strong>{slot.bookedBy}</strong>
                      {slot.bookedByEmail && <span> ({slot.bookedByEmail})</span>}
                    </Typography>
                    {slot.coachingSessionName && (
                      <Typography variant="body2" color="text.secondary">
                        Coaching Session: <strong>{slot.coachingSessionName}</strong>
                      </Typography>
                    )}
                    {slot.programName && (
                      <Typography variant="body2" color="text.secondary">
                        Program: <strong>{slot.programName}</strong>
                      </Typography>
                    )}
                    {slot.bookingCount && slot.bookingCount > 1 && (
                      <Typography variant="body2" color="text.secondary">
                        {slot.bookingCount} bookings for this slot
                      </Typography>
                    )}
                  </Box>
                )}
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{slot.pricingTier} Pricing</span>
                        {getPricingTierIcon(slot.pricingTier)}
                      </Box>
                    }
                    variant="outlined"
                    size="small"
                  />
                  {slot.priceOverride && (
                    <Chip
                      label={`$${slot.priceOverride}`}
                      variant="outlined"
                      size="small"
                      color="primary"
                    />
                  )}
                </Stack>
              </Box>
            )}
          </CardContent>
          <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={saveEditedSlot}
                  variant="contained"
                  color="primary"
                >
                  Save
                </Button>
                <Button
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={cancelEditingSlot}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => startEditingSlot(index, slot, dayOfWeek, dayOfMonth)}
                  variant="outlined"
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => removeTimeSlot(index, dayOfWeek, dayOfMonth)}
                  variant="outlined"
                >
                  Delete
                </Button>
              </Box>
            )}
          </CardActions>
        </Card>
      );
    };

    switch (formData.frequency) {
      case 'daily':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Daily Time Slots</Typography>
            {formData.dailySchedule.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No time slots defined
              </Typography>
            ) : (
              formData.dailySchedule.map((slot, index) =>
                renderSlotCard(slot, index)
              )
            )}
          </Box>
        );
      case 'weekly':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Weekly Schedule</Typography>
            {daysOfWeek.map((dayName, dayOfWeek) => {
              const daySchedule = formData.weeklySchedule.find(day => day.dayOfWeek === dayOfWeek);
              return (
                <Box key={dayOfWeek} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {dayName}
                  </Typography>
                  {!daySchedule || daySchedule.slots.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No slots defined
                    </Typography>
                  ) : (
                    daySchedule.slots.map((slot, index) =>
                      renderSlotCard(slot, index, dayOfWeek)
                    )
                  )}
                </Box>
              );
            })}
          </Box>
        );
      case 'monthly':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Monthly Schedule</Typography>
            {formData.monthlySchedule.map((monthSchedule) => (
              <Box key={monthSchedule.dayOfMonth} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Day {monthSchedule.dayOfMonth}
                </Typography>
                {monthSchedule.slots.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No slots defined
                  </Typography>
                ) : (
                  monthSchedule.slots.map((slot, index) =>
                    renderSlotCard(slot, index, undefined, monthSchedule.dayOfMonth)
                  )
                )}
              </Box>
            ))}
          </Box>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.COACH, UserRole.EMPLOYEE, UserRole.SUPER_ADMIN]}>
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Button
            component={RouterLink}
            to="/amenities"
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Amenity Management
          </Button>
          <Typography color="text.primary">{amenity?.name} Schedule</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate('/amenities')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <ScheduleIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {amenity?.name} Schedule
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Refresh View">
              <Button
                variant="outlined"
                onClick={() => {
                  fetchSchedules();
                  fetchCalendarData(selectedDate);
                }}
                disabled={loading}
              >
                <RefreshIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Copy schedules from another amenity">
              <Button
                variant="outlined"
                onClick={handleCopySchedules}
              >
                <ContentCopyIcon />
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSchedule}
            >
              Schedule
            </Button>
          </Stack>
        </Box>

        {/* Error Alert - Only show on main page if not in dialog */}
        {error && !openCreateDialog && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Amenity Info */}
        {amenity && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Amenity Details</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography><strong>Name:</strong> {amenity.name}</Typography>
                <Typography><strong>Type:</strong> {amenity.type}</Typography>
                <Typography><strong>Location:</strong> {amenity.location || 'Not specified'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography><strong>Available:</strong> {amenity.available ? 'Yes' : 'No'}</Typography>
                <Typography><strong>Max Bookings Per Slot:</strong> {amenity.maxBookingsPerSlot}</Typography>
                <Typography><strong>Booking Fee:</strong> ${amenity.bookingFee || 0}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Schedules List */}
        <Grid container spacing={3}>
          {schedules.map((schedule) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={schedule.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h2" noWrap>
                      {schedule.name}
                    </Typography>
                    <Chip
                      label={schedule.active ? 'Active' : 'Inactive'}
                      color={schedule.active ? 'success' : 'default'}
                      size="small"
                      icon={schedule.active ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                  </Box>

                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {schedule.schedulePattern.frequency.charAt(0).toUpperCase() + schedule.schedulePattern.frequency.slice(1)} Schedule
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DateRangeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(schedule.effectiveStartDate)} - {schedule.effectiveEndDate ? formatDate(schedule.effectiveEndDate) : 'Ongoing'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Slots: {schedule.minBookingDurationMinutes} min × {schedule.maxConsecutiveSlots} max (up to {schedule.maxBookingDurationMinutes} min)
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Priority: {schedule.priority}
                      </Typography>
                    </Box>

                    {schedule.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {schedule.description}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditSchedule(schedule)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteSchedule(schedule)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {schedules.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No schedules found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first schedule to define when this amenity is available
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateSchedule}>
              Create Schedule
            </Button>
          </Paper>
        )}

        {/* Effective Schedule Calendar */}
        <Paper sx={{ p: 3, mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              <CalendarTodayIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Daily Schedule View
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigateDate('prev')} size="small" sx={{ mr: 1 }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mx: 2 }}>
                {formatDate(formatDateForInput(selectedDate))}
              </Typography>
              <IconButton onClick={() => navigateDate('next')} size="small" sx={{ ml: 1 }}>
                <ChevronRightIcon />
              </IconButton>

              <Button
                title="Jump to a specific date"
                size="small"
                onClick={openDatePickerDialog}
                sx={{ ml: 1 }}
              >
                <DateRangeIcon style={{ fontSize: 'medium' }} />
              </Button>

              <Button
                size="small"
                onClick={goToToday}
                title="Go to today"
                style={{ fontSize: 'medium' }}
              >
                <TodayIcon style={{ fontSize: 'medium' }} />
              </Button>

              <IconButton
                onClick={() => fetchCalendarData(selectedDate)}
                size="small"
                disabled={calendarLoading}
                sx={{ ml: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Full day view (12 AM - 12 AM) • No overlapping schedules allowed
            </Typography>
            {Array.isArray(calendarSlots) && calendarSlots.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Time blocks: {minBookingDuration} minutes •
                Booking duration: {minBookingDuration}-{maxBookingDuration} minutes
              </Typography>
            )}
          </Box>

          {calendarError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {calendarError}
            </Alert>
          )}

          {calendarLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {!Array.isArray(calendarSlots) || calendarSlots.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <CalendarTodayIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No schedule available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No time slots are defined for this date
                  </Typography>
                </Paper>
              ) : (
                <>
                  {/* Calendar Grid with scrolling */}
                  <Box
                    ref={calendarContainerRef}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      boxShadow: 1,
                      maxHeight: '70vh',
                      overflowY: 'auto'
                    }}
                  >
                    {generateTimeBlocks().map((block, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          minHeight: 60,
                          borderBottom: index < generateTimeBlocks().length - 1 ? 1 : 0,
                          borderColor: 'divider',
                          '&:hover': {
                            transform: 'scale(1.002)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            zIndex: 1,
                            position: 'relative'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        {/* Time column */}
                        <Box
                          sx={{
                            width: 120,
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRight: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2
                          }}
                          data-time={block.startTime}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.85rem' }}>
                            {block.displayTime}
                          </Typography>
                        </Box>

                        {/* Status block */}
                        <Box
                          onClick={() => {
                            // If slot is booked/on-hold and has booking ID, navigate to booking detail
                            if (block.bookingId && (block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD)) {
                              navigate(`/amenities/${amenityId}/bookings/${block.bookingId}`);
                            }
                            // If slot is available and has a schedule, open booking dialog
                            else if (block.hasSchedule && block.status === SlotStatus.AVAILABLE) {
                              setBookingDialogDate(selectedDate);
                              setBookingDialogSlot({ startTime: block.startTime, endTime: block.endTime });
                              setOpenBookingDialog(true);
                            }
                          }}
                          sx={{
                            flex: 1,
                            bgcolor: getStatusColor(block.status, block.hasSchedule),
                            color: getStatusTextColor(block.status, block.hasSchedule),
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            position: 'relative',
                            cursor: (block.bookingId && (block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD)) || (block.hasSchedule && block.status === SlotStatus.AVAILABLE) ? 'pointer' : 'default',
                            '&:hover': (block.bookingId && (block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD)) || (block.hasSchedule && block.status === SlotStatus.AVAILABLE) ? {
                              opacity: 0.8,
                              boxShadow: 1
                            } : {}
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {block.hasSchedule ? (
                                (block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD) && block.bookedBy
                                  ? `Booked by ${block.bookedBy}`
                                  : getSlotStatusText(block.status)
                              ) : 'No Schedule'}
                            </Typography>
                            {block.hasSchedule && (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    {block.status === SlotStatus.OUT_OF_SCHEDULE ? 'out of schedule' : getPricingTierText(block.pricingTier)}
                                    {block.priceOverride && ` • Special: $${block.priceOverride.toFixed(2)}`}
                                  </Typography>
                                  {block.status !== SlotStatus.OUT_OF_SCHEDULE && getPricingTierIcon(block.pricingTier)}
                                </Box>
                                {(block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD) && block.bookedByEmail && (
                                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                                    {block.bookedByEmail}
                                  </Typography>
                                )}
                                {(block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD) && block.coachingSessionName && (
                                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontStyle: 'italic' }}>
                                    Session: {block.coachingSessionName}
                                  </Typography>
                                )}
                                {(block.status === SlotStatus.BOOKED || block.status === SlotStatus.OUT_OF_SCHEDULE || block.status === SlotStatus.ON_HOLD) && block.programName && (
                                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontStyle: 'italic' }}>
                                    Program: {block.programName}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>

                          {block.hasSchedule && (
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="caption" sx={{ opacity: 0.8, fontFamily: 'monospace' }}>
                                {block.startTime} - {block.endTime}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Legend */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Status Legend:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: getStatusColor(SlotStatus.AVAILABLE, true),
                              borderRadius: 1
                            }}
                          />
                          <Typography variant="body2">Available</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: getStatusColor(SlotStatus.BOOKED, true),
                              borderRadius: 1
                            }}
                          />
                          <Typography variant="body2">Booked</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: getStatusColor(SlotStatus.ON_HOLD, true),
                              borderRadius: 1
                            }}
                          />
                          <Typography variant="body2">On Hold</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: getStatusColor(SlotStatus.UNAVAILABLE, true),
                              borderRadius: 1
                            }}
                          />
                          <Typography variant="body2">Unavailable</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: getStatusColor(SlotStatus.UNAVAILABLE, false),
                              borderRadius: 1
                            }}
                          />
                          <Typography variant="body2">No Schedule</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog
          open={openCreateDialog}
          disableEscapeKeyDown={true}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isEditing ? 'Edit Schedule' : 'Create New Schedule'}
          </DialogTitle>
          <DialogContent ref={dialogContentRef}>
            {/* Error Alert within Dialog */}
            {error && openCreateDialog && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Typography variant="h6">Basic Information</Typography>

              <TextField
                label="Schedule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                error={!formData.name.trim()}
                helperText={!formData.name.trim() ? 'Schedule name is required' : ''}
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequency"
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Effective Start Date"
                  type="date"
                  value={formData.effectiveStartDate}
                  onChange={(e) => setFormData({ ...formData, effectiveStartDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                  error={!formData.effectiveStartDate}
                  helperText={!formData.effectiveStartDate ? 'Effective start date is required' : ''}
                />

                <TextField
                  label="Effective End Date"
                  type="date"
                  value={formData.effectiveEndDate}
                  onChange={(e) => setFormData({ ...formData, effectiveEndDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Min Booking Duration (min)</InputLabel>
                  <Select
                    value={formData.minBookingDurationMinutes}
                    onChange={(e) => {
                      const newMinDuration = e.target.value as number;
                      setFormData({ 
                        ...formData, 
                        minBookingDurationMinutes: newMinDuration,
                        maxBookingDurationMinutes: formData.maxConsecutiveSlots * newMinDuration
                      });
                    }}
                    label="Min Booking Duration (min)"
                  >
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={45}>45 minutes</MenuItem>
                    <MenuItem value={60}>60 minutes</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Max Consecutive Slots"
                  type="number"
                  value={formData.maxConsecutiveSlots}
                  onChange={(e) => {
                    const newMaxSlots = parseInt(e.target.value) || 1;
                    setFormData({ 
                      ...formData, 
                      maxConsecutiveSlots: newMaxSlots,
                      maxBookingDurationMinutes: newMaxSlots * formData.minBookingDurationMinutes
                    });
                  }}
                  InputProps={{ inputProps: { min: 1, step: 1 } }}
                  fullWidth
                />
              </Box>

              <TextField
                label="Max Duration (calculated)"
                value={`${formData.maxBookingDurationMinutes} minutes (${Math.floor(formData.maxBookingDurationMinutes / 60)}h ${formData.maxBookingDurationMinutes % 60}m)`}
                InputProps={{ readOnly: true }}
                disabled
                fullWidth
                helperText="Auto-calculated: Max Consecutive Slots × Min Duration"
              />

              {/* NEW: Slot generation info */}
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Slot-Based Booking System
                </Typography>
                <Typography variant="caption" component="div">
                  • <strong>Min Duration</strong> must be 15, 30, 45, or 60 minutes and defines fixed time slots
                </Typography>
                <Typography variant="caption" component="div">
                  • <strong>Max Consecutive Slots</strong> limits how many slots can be booked in a row
                </Typography>
                <Typography variant="caption" component="div">
                  • Example: 30 min slots, max 4 slots = members can book up to 2 hours (8:00-8:30, 8:30-9:00, 9:00-9:30, 9:30-10:00)
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic', color: 'warning.main' }}>
                  ⚠️ All schedules for this amenity must use the same Min Duration
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                  InputProps={{ inputProps: { min: 1 } }}
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Box>

              <Divider />

              {/* Court Use Restrictions */}
              <Typography variant="h6">Court Use Restrictions</Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.courtUseRestrictions}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      courtUseRestrictions: e.target.checked 
                    })}
                  />
                }
                label="Enable player count restrictions"
              />

              <Collapse in={formData.courtUseRestrictions}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Minimum Players"
                    type="number"
                    value={formData.minPlayers}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setFormData({ 
                        ...formData, 
                        minPlayers: Math.max(1, val),
                        maxPlayers: Math.max(val, formData.maxPlayers)
                      });
                    }}
                    InputProps={{ inputProps: { min: 1, step: 1 } }}
                    fullWidth
                    helperText="Including the host member"
                  />

                  <TextField
                    label="Maximum Players"
                    type="number"
                    value={formData.maxPlayers}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 2;
                      setFormData({ 
                        ...formData, 
                        maxPlayers: Math.max(formData.minPlayers, val)
                      });
                    }}
                    InputProps={{ inputProps: { min: formData.minPlayers, step: 1 } }}
                    fullWidth
                    helperText="Including the host member"
                  />
                </Box>
              </Collapse>

              <Divider />

              {/* Tier Access Restrictions */}
              <Typography variant="h6">Tier Access Restrictions</Typography>
              
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={membershipTiers
                    .filter(tier => {
                      if (!amenity?.tierAccess) return true;
                      const accessLevel = amenity.tierAccess[tier.name];
                      return accessLevel !== TierAccessLevel.NO_ACCESS;
                    })
                    .map(tier => tier.name)}
                  value={formData.availableToTiers}
                  onChange={(_, newValue) => setFormData({ ...formData, availableToTiers: newValue })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Available to Tiers"
                      placeholder="Select tiers (leave empty for all)"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        color="primary"
                        size="small"
                      />
                    ))
                  }
                />
                <FormHelperText>
                  {formData.availableToTiers.length === 0 
                    ? 'All tiers with access to this amenity can book during this schedule'
                    : 'Only the selected tiers can book during this schedule (must also have amenity-level access)'}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Time Slots */}
              <Typography variant="h6">Time Slots</Typography>

              {/* Show validation message for time slots */}
              {!isFormValid() && formData.name.trim() && formData.effectiveStartDate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  At least one time slot must be defined for the schedule to be valid.
                </Alert>
              )}

              {/* Add new time slot section */}
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" gutterBottom>Add Time Slot</Typography>

                {formData.frequency === 'weekly' && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Day of Week</InputLabel>
                    <Select
                      value={selectedDayOfWeek}
                      label="Day of Week"
                      onChange={(e) => setSelectedDayOfWeek(e.target.value as number)}
                    >
                      {daysOfWeek.map((day, index) => (
                        <MenuItem key={index} value={index}>{day}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {formData.frequency === 'monthly' && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Day of Month</InputLabel>
                    <Select
                      value={selectedDayOfMonth}
                      label="Day of Month"
                      onChange={(e) => setSelectedDayOfMonth(e.target.value as number)}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <MenuItem key={day} value={day}>{day}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={newSlot.startTime}
                    onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <TextField
                    label="End Time"
                    type="time"
                    value={newSlot.endTime}
                    onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={newSlot.status}
                      label="Status"
                      onChange={(e) => setNewSlot({ ...newSlot, status: e.target.value as SlotStatus })}
                    >
                      <MenuItem value={SlotStatus.AVAILABLE}>Available</MenuItem>
                      <MenuItem value={SlotStatus.UNAVAILABLE}>Unavailable</MenuItem>
                      <MenuItem value={SlotStatus.ON_HOLD}>On Hold</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Pricing Tier</InputLabel>
                    <Select
                      value={newSlot.pricingTier}
                      label="Pricing Tier"
                      onChange={(e) => setNewSlot({ ...newSlot, pricingTier: e.target.value as PricingTier })}
                    >
                      <MenuItem value={PricingTier.OFF_PEAK}>Off Peak</MenuItem>
                      <MenuItem value={PricingTier.NORMAL}>Normal</MenuItem>
                      <MenuItem value={PricingTier.PEAK}>Peak</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Price Override"
                    type="number"
                    value={newSlot.priceOverride}
                    onChange={(e) => setNewSlot({ ...newSlot, priceOverride: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    fullWidth
                  />
                </Box>

                <Button onClick={addTimeSlot} variant="outlined" startIcon={<AddIcon />}>
                  Add Time Slot
                </Button>
              </Paper>

              {/* Display existing time slots */}
              {renderTimeSlots()}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenCreateDialog(false);
              setError(null); // Clear error when dialog is closed
              setEditingSlot(null); // Clear editing state when dialog is closed
            }}>Cancel</Button>

            {!isFormValid() && (
              <Typography variant="caption" color="error" sx={{ mr: 2 }}>
                {getValidationMessage()}
              </Typography>
            )}

            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isFormValid()}
            >
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} disableEscapeKeyDown={true}>
          <DialogTitle>Delete Schedule</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedSchedule?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDeleteDialog(false);
              setError(null); // Clear error when dialog is closed
              setEditingSlot(null); // Clear editing state when dialog is closed
            }}>Cancel</Button>
            <Button onClick={confirmDeleteSchedule} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Copy Schedules Dialog */}
        <Dialog
          open={openCopySchedulesDialog}
          onClose={() => setOpenCopySchedulesDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ContentCopyIcon />
              Copy Schedules from Another Amenity
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Select an amenity to copy all of its schedules to "{amenity?.name}".
                The copied schedules will have "- Copy" appended to their names.
              </Typography>

              <FormControl fullWidth required>
                <InputLabel id="source-amenity-label">Select Amenity</InputLabel>
                <Select
                  labelId="source-amenity-label"
                  value={selectedSourceAmenity}
                  onChange={(e) => setSelectedSourceAmenity(e.target.value)}
                  label="Select Amenity"
                >
                  {availableAmenities.map((amenity) => (
                    <MenuItem key={amenity.id} value={amenity.id}>
                      {amenity.name} ({amenity.type})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {availableAmenities.length === 0
                    ? 'No other amenities available to copy from'
                    : `${availableAmenities.length} amenit${availableAmenities.length > 1 ? 'ies' : 'y'} available`
                  }
                </FormHelperText>
              </FormControl>

              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenCopySchedulesDialog(false);
                setSelectedSourceAmenity('');
                setError(null);
              }}
              disabled={copyingSchedules}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmCopySchedules}
              variant="contained"
              disabled={!selectedSourceAmenity || copyingSchedules || availableAmenities.length === 0}
              startIcon={copyingSchedules ? <CircularProgress size={20} /> : <ContentCopyIcon />}
            >
              {copyingSchedules ? 'Copying...' : 'Copy Schedules'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date Picker Dialog */}
        <Dialog open={openDatePicker} onClose={handleDatePickerCancel} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DateRangeIcon sx={{ mr: 1 }} />
              Jump to Date
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Select Date"
                type="date"
                value={tempSelectedDate}
                onChange={(e) => setTempSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDatePickerCancel}>Cancel</Button>
            <Button
              onClick={handleDatePickerConfirm}
              variant="contained"
              disabled={!tempSelectedDate}
            >
              Go to Date
            </Button>
          </DialogActions>
        </Dialog>

        {/* Booking Dialog */}
        <BookAmenityDialog
          open={openBookingDialog}
          onClose={() => {
            setOpenBookingDialog(false);
            setBookingDialogDate(null);
            setBookingDialogSlot(null);
          }}
          amenity={amenity}
          onSuccess={(message) => {
            setSuccessMessage(message || 'Booking created successfully!');
            // Refresh calendar data to show the new booking
            fetchCalendarData(selectedDate);
          }}
          preselectedDate={bookingDialogDate}
          preselectedSlot={bookingDialogSlot}
        />
      </Box>
      </AdminAccessControl>
    </Container>
  );
};

export default AmenitySchedulePage;
